import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';

type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

/**
 * docs/DESIGN_SYSTEM.md §4.3 — "trigger styled identically to a text
 * input." Uses a native <select> rather than a custom popover: simpler,
 * fully keyboard/screen-reader accessible for free, and the visual
 * difference from a bespoke dropdown is negligible at this component's
 * scale (single-choice settings fields, not a searchable command menu).
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(({ className = '', children, ...props }, ref) => {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={[
          'h-9 w-full appearance-none rounded-sm bg-bg-base border border-border-subtle px-3 pr-9 text-body text-text-primary',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-management-500 focus:border-border-strong',
          className,
        ].join(' ')}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        size={14}
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary"
        aria-hidden="true"
      />
    </div>
  );
});

Select.displayName = 'Select';
